import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError, API, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { TextField, SelectField, AreaField } from "@/components/common";
import { LEAD_STATUS, PRIORITY, COMM_TYPE, COMM_DIRECTION, CONTACT_RESULT, TASK_TYPE, PRIORITY_COLORS, usd, fmtDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { buildPropertyResearchLinks, RESEARCH_SITES, normalizeAddressClient } from "@/lib/research";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ArrowLeft, Phone, Mail, MapPin, Plus, User, Lock, Eye, EyeOff, ShieldCheck, Sparkles, RefreshCw, Copy, FileText, Gauge, FileSignature } from "lucide-react";
import CountyRecordsLinks from "@/components/CountyRecordsLinks";

// Admin roles allowed to manage per-deal owner-identity disclosure. Must mirror backend ADMIN_ROLES.
const DISCLOSURE_ROLES = ["System Owner", "Administrator"];

function ownerDisplayName(owner) {
  if (!owner) return "Owner name unavailable";
  if (owner.identity_masked) return owner.display_name || "Owner (identity protected)";
  const name = [owner.first_name, owner.last_name].filter(Boolean).join(" ").trim();
  return name || "Owner name unavailable";
}

export default function LeadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [county, setCounty] = useState(null);
  const load = () => api.get(`/leads/${id}`).then((r) => setLead(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when the lead id changes; load closes over the same id
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    const st = lead?.property?.state;
    const co = lead?.property?.county;
    if (st && co) {
      api.get("/county-offices/match", { params: { state: st, county: co } })
        .then((r) => setCounty(r.data)).catch(() => setCounty(null));
    } else { setCounty(null); }
  }, [lead?.property?.state, lead?.property?.county]);
  if (!lead) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const updateField = async (patch) => {
    setLead((p) => ({ ...p, ...patch }));
    await api.put(`/leads/${id}`, patch);
    toast.success("Lead updated");
  };

  const o = lead.owner || {}, pr = lead.property || {};
  const canManageDisclosure = DISCLOSURE_ROLES.includes(user?.role);

  return (
    <div>
      <PageHeader title={ownerDisplayName(lead.owner)} subtitle={`${lead.lead_id_display} · ${pr.property_address || ""}`}
        action={<div className="flex gap-2"><Link to={`/forms?lead_id=${lead.id}`}><Button variant="outline" data-testid="fill-form-from-lead"><FileSignature className="w-4 h-4 mr-1" /> Fill a Form</Button></Link><Link to="/leads"><Button variant="outline" data-testid="back-to-leads"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button></Link></div>} />

      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex flex-wrap gap-4 items-center bg-card border border-border rounded-md p-4">
          <div className="flex-1 min-w-[180px]">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Status</div>
            <Select value={lead.status} onValueChange={(v) => updateField({ status: v })}>
              <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{LEAD_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[160px]">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Priority</div>
            <Select value={lead.priority} onValueChange={(v) => updateField({ priority: v })}>
              <SelectTrigger data-testid="lead-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PRIORITY.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[140px]"><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Asking Price</div><div className="font-display text-xl font-bold font-mono-nums">{usd(lead.asking_price)}</div></div>
          <div className="flex-1 min-w-[140px]"><div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Est. Market Value</div><div className="font-display text-xl font-bold font-mono-nums">{usd(pr.estimated_market_value)}</div></div>
        </div>

        <Tabs defaultValue="overview">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="comms" data-testid="tab-comms">Communications ({lead.communications.length})</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks ({lead.tasks.length})</TabsTrigger>
            <TabsTrigger value="offers" data-testid="tab-offers">Offers ({lead.offers.length})</TabsTrigger>
            <TabsTrigger value="docs" data-testid="tab-docs">Documents ({lead.documents.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-5 grid lg:grid-cols-2 gap-6">
            <Section title="Seller / Owner" action={canManageDisclosure ? <OwnerDisclosureToggle lead={lead} reload={load} /> : null}>
              <Row icon={o.identity_masked ? Lock : User} label="Name" value={<span data-testid="ld-owner-name">{ownerDisplayName(lead.owner)}</span>} />
              <Row icon={Phone} label="Phone" value={o.phone} />
              <Row icon={Mail} label="Email" value={o.email} />
              <Row icon={MapPin} label="Mailing" value={o.mailing_address} />
              <Row label="Preferred Contact" value={o.preferred_contact_method} />
              <Row label="Authorized to Sell" value={o.authorized_to_sell ? "Yes" : "No"} />
              <div className="pt-2"><AreaField label="Seller Motivation" value={lead.seller_motivation} onChange={(v) => setLead((p) => ({ ...p, seller_motivation: v }))} testid="ld-motivation" />
                <Button size="sm" variant="outline" className="mt-2" onClick={() => updateField({ seller_motivation: lead.seller_motivation })} data-testid="ld-save-motivation">Save</Button></div>
            </Section>
            <Section title="Property">
              <Row icon={MapPin} label="Address" value={`${pr.property_address || ""}, ${pr.city || ""}, ${pr.state || ""} ${pr.zip_code || ""}`} />
              <Row label="Type" value={pr.property_type} />
              <Row label="Beds / Baths" value={`${pr.bedrooms ?? "—"} / ${pr.bathrooms ?? "—"}`} />
              <Row label="Square Feet" value={pr.square_feet} />
              <Row label="Year Built" value={pr.year_built} />
              <Row label="Occupancy" value={pr.occupancy_status} />
              <Row label="Condition" value={pr.property_condition} />
              <Row label="Est. Rent" value={pr.estimated_rent ? usd(pr.estimated_rent) : "—"} />
              <Row label="Foreclosure" value={lead.foreclosure_involved ? "Yes" : "No"} />
            </Section>
            <div className="lg:col-span-2">
              <PropertyIntelligence lead={lead} />
            </div>
            <div className="lg:col-span-2">
              <PropertyPhotos lead={lead} reload={load} />
            </div>
            <div className="lg:col-span-2">
              <LeadAiPanel lead={lead} />
            </div>
            <div className="lg:col-span-2">
              <CountyRecordsLinks office={county} hasCounty={!!(lead.property?.state && lead.property?.county)} verifiedBy="Acquisition Manager" />
            </div>
          </TabsContent>

          <TabsContent value="comms" className="mt-5">
            <CommsTab lead={lead} reload={load} />
          </TabsContent>
          <TabsContent value="tasks" className="mt-5">
            <TasksTab lead={lead} reload={load} />
          </TabsContent>
          <TabsContent value="offers" className="mt-5">
            <SimpleList items={lead.offers} render={(o) => <div className="flex justify-between"><span>{o.offer_type} · {o.offer_status}</span><span className="font-mono-nums">{usd(o.offer_amount)}</span></div>} empty="No offers yet — create one on the Offers page." />
          </TabsContent>
          <TabsContent value="docs" className="mt-5">
            <SimpleList items={lead.documents} render={(d) => <div className="flex justify-between"><span>{d.document_name || d.original_filename}</span><span className="text-muted-foreground text-sm">{d.document_type}</span></div>} empty="No documents — upload on the Documents page." />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function Section({ title, action, children }) {
  return <div className="bg-card border border-border rounded-md"><div className="px-5 py-3 border-b border-border font-display font-bold flex items-center justify-between gap-3"><span>{title}</span>{action}</div><div className="p-5 space-y-3">{children}</div></div>;
}

function LeadAiPanel({ lead }) {
  const [status, setStatus] = useState(null);
  const [outputs, setOutputs] = useState({});
  const [busy, setBusy] = useState("");
  const [terms, setTerms] = useState({ offer_type: "Cash", offer_amount: lead.asking_price || "", closing_days: 30 });

  useEffect(() => {
    api.get("/ai/status").then((r) => setStatus(r.data)).catch(() => setStatus({ enabled: false }));
  }, []);
  const loadOutputs = useCallback(() => api.get(`/ai/outputs/${lead.id}`).then((r) => setOutputs(r.data.outputs || {})).catch(() => {}), [lead.id]);
  useEffect(() => { if (status?.enabled) loadOutputs(); }, [status?.enabled, loadOutputs]);

  const run = async (feature, path, body) => {
    setBusy(feature);
    try {
      const { data } = await api.post(path, { lead_id: lead.id, ...body });
      setOutputs((o) => ({ ...o, [feature]: { output: data.output, generated_at: data.generated_at, stale: false } }));
      setStatus((s) => ({ ...s, remaining: data.remaining, used: data.used }));
      toast.success(data.cached ? "Loaded existing result (no AI charge)" : "AI result ready");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && (d.message || d)) || formatApiError(e));
    } finally { setBusy(""); }
  };

  if (status && !status.enabled) {
    return (
      <div className="bg-card border border-border rounded-md" data-testid="lead-ai-disabled">
        <div className="px-5 py-3 border-b border-border font-display font-bold flex items-center gap-2"><Sparkles className="w-4 h-4 text-accent" /> AI Deal Engine</div>
        <div className="p-5 text-sm text-muted-foreground">AI features are not enabled in this release.</div>
      </div>
    );
  }
  if (!status) return null;

  const remaining = status.remaining ?? 0;
  const capped = remaining <= 0;
  return (
    <div className="bg-card border border-border rounded-md" data-testid="lead-ai-panel">
      <div className="px-5 py-3 border-b border-border font-display font-bold flex items-center justify-between gap-3">
        <span className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-accent" /> AI Deal Engine</span>
        <span className="text-[11px] font-normal text-muted-foreground" data-testid="lead-ai-counter">{remaining} of {status.limit} generations left this month</span>
      </div>
      <div className="p-5 grid md:grid-cols-2 gap-5">
        <SellerMotivationCard data={outputs.seller_motivation} busy={busy === "seller_motivation"} capped={capped}
          onRun={() => run("seller_motivation", "/ai/seller-motivation", {})}
          onRegen={() => run("seller_motivation", "/ai/seller-motivation", { force: true })} />
        <LeadSummaryCard data={outputs.lead_summary} busy={busy === "lead_summary"} capped={capped}
          onRun={() => run("lead_summary", "/ai/lead-summary", {})}
          onRegen={() => run("lead_summary", "/ai/lead-summary", { force: true })} />
        <div className="md:col-span-2">
          <OfferPriceCard data={outputs.offer_price} busy={busy === "offer_price"} capped={capped}
            onRun={() => run("offer_price", "/ai/offer-price", {})}
            onRegen={() => run("offer_price", "/ai/offer-price", { force: true })}
            onUseInLetter={(amount) => {
              const t = { ...terms, offer_amount: amount };
              setTerms(t);
              run("offer_letter", "/ai/offer-letter", { terms: t, force: true });
              toast.success(`Drafting offer letter at ${usd(amount)}…`);
            }} />
        </div>
        <div className="md:col-span-2">
          <OfferLetterCard data={outputs.offer_letter} busy={busy === "offer_letter"} capped={capped} terms={terms} setTerms={setTerms}
            onRun={(force) => run("offer_letter", "/ai/offer-letter", { terms, force })} />
        </div>
      </div>
    </div>
  );
}

const MOTIV_BAND = {
  "Low": "bg-slate-500/10 border-slate-500/40 text-slate-500",
  "Moderate": "bg-amber-500/10 border-amber-500/40 text-amber-600 dark:text-amber-500",
  "High": "bg-orange-500/10 border-orange-500/40 text-orange-600 dark:text-orange-400",
  "Very High": "bg-emerald-500/10 border-emerald-500/40 text-emerald-600 dark:text-emerald-400",
};

function AiCardShell({ icon: Icon, title, data, busy, capped, onRun, onRegen, empty, children }) {
  const has = !!data?.output;
  return (
    <div className="border border-border rounded-md" data-testid={`ai-${title.toLowerCase().replace(/[^a-z]/g, "-")}-card`}>
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-2">
        <span className="font-display font-bold text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-accent" /> {title}
          {data?.stale && <span className="text-[10px] uppercase tracking-wider bg-amber-500/10 text-amber-600 border border-amber-500/30 rounded-full px-2 py-0.5" data-testid="ai-stale-badge">Stale</span>}</span>
        {has
          ? <Button size="sm" variant="outline" onClick={onRegen} disabled={busy || capped} data-testid="ai-regenerate-btn"><RefreshCw className="w-3.5 h-3.5 mr-1" />{busy ? "…" : "Regenerate"}</Button>
          : <Button size="sm" onClick={onRun} disabled={busy || capped} data-testid="ai-generate-btn">{busy ? "Analyzing…" : "Generate"}</Button>}
      </div>
      <div className="p-4 text-sm">{has ? children : <p className="text-muted-foreground text-xs" data-testid="ai-empty">{empty}</p>}
        {has && data.generated_at && <div className="mt-3 text-[11px] text-muted-foreground">Generated {fmtDate(data.generated_at)}</div>}
      </div>
    </div>
  );
}

function SellerMotivationCard({ data, busy, capped, onRun, onRegen }) {
  const o = data?.output;
  return (
    <AiCardShell icon={Gauge} title="Seller Motivation" data={data} busy={busy} capped={capped} onRun={onRun} onRegen={onRegen}
      empty="Score how motivated the seller appears from the deal signals on file.">
      {o && (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <div className="font-display text-3xl font-bold font-mono-nums" data-testid="motivation-score">{o.score}<span className="text-base text-muted-foreground">/100</span></div>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-display font-bold uppercase tracking-wider ${MOTIV_BAND[o.band] || MOTIV_BAND.Moderate}`} data-testid="motivation-band">{o.band}</span>
          </div>
          <p className="leading-relaxed" data-testid="motivation-summary">{o.summary}</p>
          {o.drivers?.length > 0 && <ul className="list-disc pl-4 text-xs text-muted-foreground space-y-0.5">{o.drivers.map((d, i) => <li key={`${i}-${d}`}>{d}</li>)}</ul>}
        </div>
      )}
    </AiCardShell>
  );
}

function LeadSummaryCard({ data, busy, capped, onRun, onRegen }) {
  const o = data?.output;
  return (
    <AiCardShell icon={Sparkles} title="Lead Summary" data={data} busy={busy} capped={capped} onRun={onRun} onRegen={onRegen}
      empty="Get a concise internal brief of this lead — signals, risks, and the next best step.">
      {o && (
        <div className="space-y-2">
          <p className="leading-relaxed" data-testid="lead-summary-text">{o.summary}</p>
          {o.key_signals?.length > 0 && <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Key signals</div><ul className="list-disc pl-4 text-xs space-y-0.5">{o.key_signals.map((x, i) => <li key={`${i}-${x}`}>{x}</li>)}</ul></div>}
          {o.risks?.length > 0 && <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Risks</div><ul className="list-disc pl-4 text-xs space-y-0.5">{o.risks.map((x, i) => <li key={`${i}-${x}`}>{x}</li>)}</ul></div>}
          <div className="pt-1"><span className="text-[11px] uppercase tracking-wider text-muted-foreground">Next step: </span><span className="font-medium">{o.recommended_next_step}</span></div>
        </div>
      )}
    </AiCardShell>
  );
}

function OfferPriceCard({ data, busy, capped, onRun, onRegen, onUseInLetter }) {
  const o = data?.output;
  const CONF = { Low: "bg-slate-500/10 border-slate-500/40 text-slate-500", Medium: "bg-amber-500/10 border-amber-500/40 text-amber-600", High: "bg-emerald-500/10 border-emerald-500/40 text-emerald-600" };
  return (
    <AiCardShell icon={Gauge} title="Suggested Offer Price" data={data} busy={busy} capped={capped} onRun={onRun} onRegen={onRegen}
      empty="Let the AI read this lead's numbers — asking price, underwriting metrics, and your company targets — and suggest an offer price with plain-English reasoning.">
      {o && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Suggested offer</div>
              <div className="font-display text-3xl font-bold font-mono-nums" data-testid="offer-price-value">{usd(o.suggested_offer)}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Range</div>
              <div className="font-mono-nums text-sm" data-testid="offer-price-range">{usd(o.offer_range_low)} – {usd(o.offer_range_high)}</div>
            </div>
            <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-display font-bold uppercase tracking-wider ${CONF[o.confidence] || CONF.Medium}`} data-testid="offer-price-confidence">{o.confidence} confidence</span>
          </div>
          <p className="leading-relaxed" data-testid="offer-price-reasoning">{o.reasoning}</p>
          {o.key_factors?.length > 0 && <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">Key factors</div><ul className="list-disc pl-4 text-xs space-y-0.5">{o.key_factors.map((x, i) => <li key={`${i}-${x}`}>{x}</li>)}</ul></div>}
          {o.negotiation_tip && <div className="text-xs bg-secondary rounded-md px-3 py-2" data-testid="offer-price-tip"><span className="font-medium">Negotiation tip: </span>{o.negotiation_tip}</div>}
          {onUseInLetter && <Button size="sm" onClick={() => onUseInLetter(o.suggested_offer)} data-testid="offer-price-to-letter"><FileText className="w-3.5 h-3.5 mr-1" /> Draft Offer Letter at {usd(o.suggested_offer)}</Button>}
          <p className="text-[11px] text-muted-foreground">AI suggestion for internal review only — always verify against your own underwriting before making an offer.</p>
        </div>
      )}
    </AiCardShell>
  );
}

function OfferLetterCard({ data, busy, capped, terms, setTerms, onRun }) {
  const o = data?.output;
  const set = (k) => (e) => setTerms((t) => ({ ...t, [k]: e.target.value }));
  const copy = async () => { try { await navigator.clipboard.writeText(o.letter); toast.success("Draft copied"); } catch { toast.error("Couldn't copy — select the text manually."); } };
  return (
    <div className="border border-border rounded-md" data-testid="ai-offer-letter-card">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between gap-2">
        <span className="font-display font-bold text-sm flex items-center gap-2"><FileText className="w-4 h-4 text-accent" /> Offer Letter Generator</span>
        <Button size="sm" variant={o ? "outline" : "default"} onClick={() => onRun(!!o)} disabled={busy || capped} data-testid="offer-letter-generate-btn">
          {busy ? "Drafting…" : o ? <><RefreshCw className="w-3.5 h-3.5 mr-1" />Redraft</> : "Generate draft"}
        </Button>
      </div>
      <div className="p-4 space-y-3">
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="text-xs space-y-1"><span className="text-muted-foreground">Offer type</span>
            <select className="w-full border border-border rounded-md px-2 py-1.5 bg-background text-sm" value={terms.offer_type} onChange={set("offer_type")} data-testid="offer-type-select">
              {["Cash", "Subject-To", "Seller Finance", "Novation"].map((x) => <option key={x} value={x}>{x}</option>)}
            </select></label>
          <label className="text-xs space-y-1"><span className="text-muted-foreground">Offer amount</span>
            <input type="number" className="w-full border border-border rounded-md px-2 py-1.5 bg-background text-sm" value={terms.offer_amount} onChange={set("offer_amount")} data-testid="offer-amount-input" /></label>
          <label className="text-xs space-y-1"><span className="text-muted-foreground">Closing (days)</span>
            <input type="number" className="w-full border border-border rounded-md px-2 py-1.5 bg-background text-sm" value={terms.closing_days} onChange={set("closing_days")} data-testid="offer-closing-input" /></label>
        </div>
        {o ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-[11px] text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid="offer-review-notice">
              <ShieldCheck className="w-3.5 h-3.5" /> Draft only — review with a licensed professional before sending. This tool never sends anything automatically.
            </div>
            <pre className="whitespace-pre-wrap text-sm bg-secondary rounded-md p-3 max-h-72 overflow-y-auto font-sans" data-testid="offer-letter-text">{o.letter}</pre>
            <Button size="sm" variant="outline" onClick={copy} data-testid="offer-letter-copy"><Copy className="w-3.5 h-3.5 mr-1" /> Copy draft</Button>
          </div>
        ) : (
          <p className="text-muted-foreground text-xs" data-testid="offer-letter-empty">Draft a reviewable, non-binding offer letter from the property and the terms above. You always review before anything is sent.</p>
        )}
      </div>
    </div>
  );
}

function PropertyIntelligence({ lead }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const pr = lead.property || {};
  const normalized = normalizeAddressClient(pr);
  const links = buildPropertyResearchLinks(normalized);

  const lookup = async (force) => {
    if (!pr.property_address) return;
    if (force) setBusy(true);
    try {
      const { data: d } = await api.post("/property/lookup", {
        street_address: pr.property_address, city: pr.city, state: pr.state, zip: pr.zip_code, force, allow_api: false });
      setData(d);
      if (force) toast.success(d.api_call_made ? "Property data refreshed from provider" : "Loaded latest cached data");
    } catch (e) { if (force) toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { lookup(false); }, [lead.id]); // eslint-disable-line

  const v = (x, money) => (x === null || x === undefined || x === "" ? <span className="text-muted-foreground">Not available</span> : (money ? usd(x) : String(x)));
  const d = data || {};
  const rows = [
    ["APN", d.apn], ["County", d.county], ["Property Type", d.property_type || pr.property_type],
    ["Owner", d.owner_name], ["Beds / Baths", d.beds != null || d.baths != null ? `${d.beds ?? "—"} / ${d.baths ?? "—"}` : null],
    ["Living SqFt", d.living_sqft], ["Lot SqFt", d.lot_sqft], ["Year Built", d.year_built || pr.year_built],
    ["Last Sale Date", d.last_sale_date && String(d.last_sale_date).slice(0, 10)], ["Last Sale Price", d.last_sale_price, 1],
    ["Assessed Value", d.assessed_value, 1], ["Est. Market Value", d.estimated_market_value || pr.estimated_market_value, 1],
    ["Est. Rent", d.estimated_rent || pr.estimated_rent, 1], ["Tax Amount", d.tax_amount, 1],
    ["Mortgage Balance", d.mortgage_balance || lead.existing_loan_balance, 1], ["Mortgage Rate", d.mortgage_interest_rate != null ? `${d.mortgage_interest_rate}%` : null],
    ["Lender", d.lender], ["Loan Type", d.loan_type], ["Est. Equity", d.estimated_equity, 1],
    ["Foreclosure", d.foreclosure_status || (lead.foreclosure_involved ? "Involved (per lead)" : null)],
    ["Listing Status", d.listing_status], ["Listing Price", d.listing_price, 1],
  ];

  return (
    <Section title="Property Intelligence" action={
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => lookup(true)} disabled={busy} data-testid="refresh-property-data">{busy ? "Refreshing…" : "Refresh Property Data"}</Button>
        <Button size="sm" onClick={() => navigate(`/analyzer?lead_id=${lead.id}`)} data-testid="send-to-analyzer">Send to Deal Analyzer</Button>
      </div>}>
      <a href={links.google_maps} target="_blank" rel="noreferrer" className="font-medium text-accent hover:underline" data-testid="normalized-address-link">{normalized || "No address on file"}</a>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground mr-1">View property on:</span>
        {RESEARCH_SITES.map(([k, label]) => (
          <a key={k} href={links[k]} target="_blank" rel="noreferrer" data-testid={`research-${k}`}
            className="text-xs border border-border rounded-full px-3 py-1 hover:bg-secondary transition-colors">{label}</a>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2.5 mt-5 text-sm">
        {rows.map(([label, val, money]) => (
          <div key={label}><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="font-mono-nums">{v(val, money)}</div></div>
        ))}
      </div>
      {d.property_data_last_updated ? <p className="text-[11px] text-muted-foreground mt-4">Source: {d.property_data_source} · updated {fmtDate(d.property_data_last_updated)} · cached, no lookup charge on page load</p>
        : (data && <p className="text-[11px] text-muted-foreground mt-4">No cached provider data yet — tap "Refresh Property Data" to pull it once (cached 30 days).</p>)}
    </Section>
  );
}

function PropertyPhotos({ lead, reload }) {
  const [svEnabled, setSvEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/streetview/status").then((r) => setSvEnabled(!!r.data.enabled)).catch(() => {}); }, []);
  const pr = lead.property || {};
  const addr = [pr.property_address, pr.city, pr.state, pr.zip_code].filter(Boolean).join(", ");
  const photos = (lead.documents || []).filter((d) => (d.content_type || "").startsWith("image/"));

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const up = (await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
        await api.post("/documents", { lead_id: lead.id, property_id: lead.property_id, document_type: "Photo",
          document_name: up.original_filename, storage_path: up.storage_path, original_filename: up.original_filename,
          content_type: up.content_type, upload_date: new Date().toISOString() });
      }
      toast.success(`${files.length} photo${files.length > 1 ? "s" : ""} added`);
      reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <Section title={`Photos (${photos.length})`} action={
      <label className="cursor-pointer" data-testid="photo-upload-label">
        <input type="file" accept="image/*" multiple className="hidden" onChange={uploadPhotos} data-testid="photo-upload-input" />
        <span className="inline-flex items-center border border-border rounded-md px-3 py-1.5 text-sm font-medium hover:bg-secondary transition-colors"><Plus className="w-3.5 h-3.5 mr-1" />{busy ? "Uploading…" : "Add Photos"}</span>
      </label>}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {svEnabled && addr && (
          <a href={`https://www.google.com/maps?q=${encodeURIComponent(addr)}&layer=c`} target="_blank" rel="noreferrer" className="relative block">
            <img src={`${API}/streetview?address=${encodeURIComponent(addr)}&size=640x400`} alt="Street view" loading="lazy"
              className="h-32 w-full object-cover rounded-md border border-border"
              onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} data-testid="streetview-photo" />
            <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">Street View</span>
          </a>
        )}
        {photos.map((d) => (
          <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer">
            <img src={fileUrl(d.storage_path)} alt={d.document_name} loading="lazy" className="h-32 w-full object-cover rounded-md border border-border hover:opacity-90 transition-opacity" data-testid={`lead-photo-${d.id}`} />
          </a>
        ))}
        {photos.length === 0 && !svEnabled && <p className="col-span-full text-sm text-muted-foreground" data-testid="photos-empty">No photos yet — use "Add Photos" to upload walkthrough pictures from your phone.</p>}
      </div>
    </Section>
  );
}

function OwnerDisclosureToggle({ lead, reload }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const disclosed = !!lead.owner_identity_disclosed;
  const confirm = async () => {
    const next = !disclosed;
    try {
      setBusy(true);
      await api.post(`/leads/${lead.id}/disclose-owner`, { disclosed: next });
      await reload();
      toast.success(next ? "Owner identity disclosed for this deal." : "Owner identity disclosure revoked.");
      setOpen(false);
    } catch (e) {
      const s = e?.response?.status;
      const map = { 401: "Your session expired. Please sign in again.", 403: "You don't have permission to change disclosure.", 404: "Lead not found.", 409: "Disclosure state changed elsewhere. Reloading.", 500: "Something went wrong. Please try again." };
      toast.error(map[s] || formatApiError(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button size="sm" variant={disclosed ? "secondary" : "outline"} onClick={() => setOpen(true)} disabled={busy} data-testid="owner-disclosure-toggle">
        {disclosed ? <EyeOff className="w-3.5 h-3.5 mr-1.5" /> : <Eye className="w-3.5 h-3.5 mr-1.5" />}
        {busy ? (disclosed ? "Revoking…" : "Disclosing…") : (disclosed ? "Revoke Disclosure" : "Disclose Owner Identity")}
      </Button>
      <AlertDialogContent data-testid="owner-disclosure-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> {disclosed ? "Revoke owner disclosure?" : "Disclose owner identity?"}</AlertDialogTitle>
          <AlertDialogDescription>
            {disclosed
              ? "Revoke access to the owner's identity for this deal? External roles are always masked regardless."
              : "Disclosing owner identity may expose protected seller information to authorized users for this specific deal only. Continue?"}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="owner-disclosure-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); confirm(); }} disabled={busy} data-testid="owner-disclosure-confirm">
            {busy ? "Working…" : (disclosed ? "Revoke" : "Disclose")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
function Row({ icon: Icon, label, value }) {
  return <div className="flex items-start gap-3 text-sm"><div className="w-40 text-muted-foreground flex items-center gap-2">{Icon && <Icon className="w-3.5 h-3.5" />}{label}</div><div className="font-medium flex-1">{value || "—"}</div></div>;
}
function SimpleList({ items, render, empty }) {
  if (!items?.length) return <div className="text-muted-foreground text-sm bg-card border border-border rounded-md p-6">{empty}</div>;
  return <div className="bg-card border border-border rounded-md divide-y divide-border">{items.map((i) => <div key={i.id} className="p-4">{render(i)}</div>)}</div>;
}

function CommsTab({ lead, reload }) {
  const [f, setF] = useState({ communication_type: "Call", direction: "Outbound", contact_result: "Connected" });
  const [open, setOpen] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const add = async () => {
    await api.post("/communications", { ...f, lead_id: lead.id, date_time: new Date().toISOString() });
    toast.success("Communication logged"); setOpen(false); setF({ communication_type: "Call", direction: "Outbound", contact_result: "Connected" }); reload();
  };
  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="mb-4" data-testid="add-comm-btn"><Plus className="w-4 h-4 mr-1" /> Log Communication</Button></DialogTrigger>
        <DialogContent><DialogHeader><DialogTitle className="font-display">Log Communication</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Type" value={f.communication_type} onChange={set("communication_type")} options={COMM_TYPE} testid="comm-type" />
            <SelectField label="Direction" value={f.direction} onChange={set("direction")} options={COMM_DIRECTION} testid="comm-direction" />
            <SelectField label="Result" value={f.contact_result} onChange={set("contact_result")} options={CONTACT_RESULT} testid="comm-result" />
            <TextField label="Follow-Up Date" type="date" value={f.follow_up_date} onChange={set("follow_up_date")} testid="comm-followup" />
            <div className="col-span-2"><AreaField label="Summary" value={f.summary} onChange={set("summary")} testid="comm-summary" /></div>
          </div>
          <DialogFooter><Button onClick={add} data-testid="comm-submit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <SimpleList items={lead.communications} empty="No communications logged." render={(c) => (
        <div><div className="flex justify-between"><span className="font-medium">{c.communication_type} · {c.direction}</span><span className="text-muted-foreground text-sm">{fmtDate(c.date_time)}</span></div><div className="text-sm text-muted-foreground mt-1">{c.contact_result} — {c.summary}</div></div>
      )} />
    </div>
  );
}

function TasksTab({ lead, reload }) {
  const [f, setF] = useState({ task_type: "Call", priority: "Normal", status: "Open" });
  const [open, setOpen] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const add = async () => {
    await api.post("/tasks", { ...f, lead_id: lead.id });
    toast.success("Task created"); setOpen(false); setF({ task_type: "Call", priority: "Normal", status: "Open" }); reload();
  };
  const toggle = async (t) => { await api.put(`/tasks/${t.id}`, { status: t.status === "Completed" ? "Open" : "Completed" }); reload(); };
  return (
    <div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button className="mb-4" data-testid="add-task-btn"><Plus className="w-4 h-4 mr-1" /> Add Task</Button></DialogTrigger>
        <DialogContent><DialogHeader><DialogTitle className="font-display">New Task</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <TextField label="Task Name" value={f.task_name} onChange={set("task_name")} testid="task-name" className="col-span-2" />
            <SelectField label="Type" value={f.task_type} onChange={set("task_type")} options={TASK_TYPE} testid="task-type" />
            <SelectField label="Priority" value={f.priority} onChange={set("priority")} options={PRIORITY} testid="task-priority" />
            <TextField label="Due Date" type="date" value={f.due_date} onChange={set("due_date")} testid="task-due" />
          </div>
          <DialogFooter><Button onClick={add} data-testid="task-submit">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <SimpleList items={lead.tasks} empty="No tasks." render={(t) => (
        <div className="flex items-center justify-between"><div><div className="font-medium">{t.task_name}</div><div className="text-xs text-muted-foreground">{t.task_type} · Due {fmtDate(t.due_date)}</div></div>
          <div className="flex items-center gap-2"><Badge variant="outline" className={PRIORITY_COLORS[t.priority]}>{t.priority}</Badge>
            <Button size="sm" variant={t.status === "Completed" ? "secondary" : "outline"} onClick={() => toggle(t)} data-testid={`task-toggle-${t.id}`}>{t.status === "Completed" ? "Completed" : "Mark done"}</Button></div></div>
      )} />
    </div>
  );
}
